/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Branch-level panel on Mesas (not single-table edit).
 * Permission: Tables.manage_layout (same as upsert_tableside_settings).
 */
import React, { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { colors } from '../../shared/theme/colors';
import { ticketColors } from '../../shared/theme/ticketColors';
import { spacing } from '../../shared/theme/spacing';
import { typography } from '../../shared/theme/typography';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { showInfo, showSuccess } from '../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { NotifyCustomerReadyPickupSwitchRow } from '../../shared/storefront/NotifyCustomerReadyPickupSwitchRow';
import {
  getTablesideSettings,
  updateTablesideSettings,
  type TablesideMode,
  type TablesideSettings,
} from '../infra/hospitalityRepo';

const DEFAULT_SETTINGS: TablesideSettings = {
  tablesideEnabled: false,
  tablesideMode: 'draft',
  tablesideGuestItemNotes: false,
  tablesideDraftIdleMinutes: 30,
  tablesideLookingIdleMinutes: 15,
  autoFreeTableOnPay: false,
  tablesideRequirePhone: false,
  respectStoreHours: true,
  notifyCustomerReadyPickup: false,
  notifyGuestReadyDineIn: false,
  tablesidePayAtTableEnabled: false,
  tablesideTipEnabled: true,
  tablesideTipSuggestedPercents: [10, 15, 20],
  tablesideFeedbackEnabled: false,
  tablesideSplitEnabled: false,
};

const MODE_OPTIONS: Array<{ id: TablesideMode; label: string }> = [
  { id: 'signals', label: 'Solo avisar' },
  { id: 'draft', label: 'Armar pedido' },
  { id: 'auto', label: 'Auto cocina' },
];

export type TablesideSettingsPanelProps = {
  branchId: string | null;
};

export function TablesideSettingsPanel({ branchId }: TablesideSettingsPanelProps) {
  const { height: windowHeight } = useWindowDimensions();
  const bodyMaxHeight = Math.max(280, Math.min(520, Math.round(windowHeight * 0.55)));
  const [expanded, setExpanded] = useState(false);
  const [settings, setSettings] = useState<TablesideSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const next = await getTablesideSettings(branchId);
      setSettings(next ?? DEFAULT_SETTINGS);
    } catch (error) {
      presentUnexpectedFailure({
        module: 'hospitality',
        op: 'tableside_settings.load',
        error,
        userMessage: 'No se pudo cargar el pedido desde la mesa.',
      });
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useFocusEffect(
    useCallback(() => {
      void loadSettings();
    }, [loadSettings]),
  );

  const handleSave = async () => {
    if (!branchId) return;

    const draftIdle = settings.tablesideDraftIdleMinutes;
    const lookingIdle = settings.tablesideLookingIdleMinutes;
    if (
      !Number.isInteger(draftIdle) ||
      draftIdle < 5 ||
      draftIdle > 240
    ) {
      showInfo('Minutos del pedido', 'Usá un número entre 5 y 240.');
      return;
    }
    if (
      !Number.isInteger(lookingIdle) ||
      lookingIdle < 5 ||
      lookingIdle > 120
    ) {
      showInfo('Minutos mirando', 'Usá un número entre 5 y 120.');
      return;
    }

    setSaving(true);
    try {
      await updateTablesideSettings(branchId, settings);
      showSuccess('Guardado', 'Pedido desde la mesa actualizado.');
    } catch (error) {
      presentUnexpectedFailure({
        module: 'hospitality',
        op: 'tableside_settings.save',
        error,
        userMessage: 'No se pudo guardar el pedido desde la mesa.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!branchId) return null;

  return (
    <View style={styles.card}>
      <TouchableOpacity
        style={styles.header}
        onPress={() => setExpanded((v) => !v)}
        accessibilityRole="button"
        accessibilityLabel={
          expanded ? 'Ocultar pedido desde la mesa' : 'Pedido desde la mesa'
        }
      >
        <View style={styles.headerCopy}>
          <Text style={styles.title}>Pedido desde la mesa</Text>
          <Text style={styles.headerHint}>
            {settings.tablesideEnabled
              ? `Prendido · ${MODE_OPTIONS.find((m) => m.id === settings.tablesideMode)?.label ?? 'Armar pedido'}`
              : 'Apagado'}
          </Text>
        </View>
        <Ionicons
          name={expanded ? 'chevron-up' : 'chevron-down'}
          size={18}
          color={ticketColors.ink2}
        />
      </TouchableOpacity>

      {expanded ? (
        loading ? (
          <ActivityIndicator color={colors.accent} style={styles.loader} />
        ) : (
          <ScrollView
            style={{ maxHeight: bodyMaxHeight }}
            contentContainerStyle={styles.body}
            keyboardShouldPersistTaps="handled"
            nestedScrollEnabled
            showsVerticalScrollIndicator
          >
            <View style={styles.switchRow}>
              <View style={styles.switchCopy}>
                <Text style={styles.label}>Pedido desde la mesa</Text>
                <Text style={styles.desc}>
                  Los clientes piden mirando el QR de la mesa.
                </Text>
              </View>
              <Switch
                value={settings.tablesideEnabled}
                onValueChange={(value) =>
                  setSettings((s) => ({ ...s, tablesideEnabled: value }))
                }
                trackColor={{ false: colors.borderSoft, true: colors.success }}
                thumbColor={colors.surface}
                accessibilityLabel="Pedido desde la mesa"
              />
            </View>

            {settings.tablesideEnabled ? (
              <>
                <Text style={styles.label}>Cómo piden</Text>
                <View style={styles.modeRow}>
                  {MODE_OPTIONS.map((opt) => {
                    const selected = settings.tablesideMode === opt.id;
                    return (
                      <TouchableOpacity
                        key={opt.id}
                        style={[styles.modeChip, selected && styles.modeChipSelected]}
                        onPress={() =>
                          setSettings((s) => ({ ...s, tablesideMode: opt.id }))
                        }
                        accessibilityRole="button"
                        accessibilityLabel={opt.label}
                      >
                        <Text
                          style={[
                            styles.modeChipText,
                            selected && styles.modeChipTextSelected,
                          ]}
                        >
                          {opt.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>

                <View style={styles.switchRow}>
                  <View style={styles.switchCopy}>
                    <Text style={styles.label}>Notas del cliente</Text>
                    <Text style={styles.desc}>
                      Si está prendido, pueden agregar una nota al sumar un ítem.
                    </Text>
                  </View>
                  <Switch
                    value={settings.tablesideGuestItemNotes}
                    onValueChange={(value) =>
                      setSettings((s) => ({ ...s, tablesideGuestItemNotes: value }))
                    }
                    trackColor={{ false: colors.borderSoft, true: colors.success }}
                    thumbColor={colors.surface}
                    accessibilityLabel="Notas del cliente"
                  />
                </View>

                <View style={styles.switchRow}>
                  <View style={styles.switchCopy}>
                    <Text style={styles.label}>Pedir teléfono al avisar</Text>
                    <Text style={styles.desc}>
                      Antes de llamar al mozo o pedir la cuenta. No bloquea mirar la carta ni sumar.
                    </Text>
                  </View>
                  <Switch
                    value={settings.tablesideRequirePhone}
                    onValueChange={(value) =>
                      setSettings((s) => ({ ...s, tablesideRequirePhone: value }))
                    }
                    trackColor={{ false: colors.borderSoft, true: colors.success }}
                    thumbColor={colors.surface}
                    accessibilityLabel="Pedir teléfono al avisar"
                    testID="hospitality.tableside.require-phone"
                  />
                </View>

                <View style={styles.switchRow}>
                  <View style={styles.switchCopy}>
                    <Text style={styles.label}>Respetar horarios de la tienda</Text>
                    <Text style={styles.desc}>
                      Si está prendido, fuera de horario solo se mira la carta. Usa los mismos
                      horarios que Tienda online. Si lo apagás, se puede pedir siempre.
                    </Text>
                  </View>
                  <Switch
                    value={settings.respectStoreHours}
                    onValueChange={(value) =>
                      setSettings((s) => ({ ...s, respectStoreHours: value }))
                    }
                    trackColor={{ false: colors.borderSoft, true: colors.success }}
                    thumbColor={colors.surface}
                    accessibilityLabel="Respetar horarios de la tienda"
                    testID="hospitality.tableside.respect-hours"
                  />
                </View>

                <View style={styles.fieldsRow}>
                  <View style={styles.field}>
                    <Text style={styles.label}>Minutos sin tocar el pedido</Text>
                    <Text style={styles.desc}>
                      Si no piden nada, la mesa se limpia sola a los X min (sin cuenta ni cocina).
                    </Text>
                    <TextInput
                      style={modalExperience.formInput}
                      value={String(settings.tablesideDraftIdleMinutes)}
                      onChangeText={(value) =>
                        setSettings((s) => ({
                          ...s,
                          tablesideDraftIdleMinutes: Number(value) || 0,
                        }))
                      }
                      keyboardType="numeric"
                      accessibilityLabel="Minutos sin tocar el pedido"
                    />
                  </View>
                  <View style={styles.field}>
                    <Text style={styles.label}>Minutos mirando sin sumar</Text>
                    <Text style={styles.desc}>
                      Si solo miran la carta y se van, dejamos de marcarlos como “mirando”.
                    </Text>
                    <TextInput
                      style={modalExperience.formInput}
                      value={String(settings.tablesideLookingIdleMinutes)}
                      onChangeText={(value) =>
                        setSettings((s) => ({
                          ...s,
                          tablesideLookingIdleMinutes: Number(value) || 0,
                        }))
                      }
                      keyboardType="numeric"
                      accessibilityLabel="Minutos mirando sin sumar"
                    />
                  </View>
                </View>
              </>
            ) : null}

            <View style={styles.switchRow}>
              <View style={styles.switchCopy}>
                <Text style={styles.label}>Liberar mesa al cobrar</Text>
                <Text style={styles.desc}>
                  Si está apagado, la mesa sigue ocupada después de pagar hasta que
                  toques Liberar.
                </Text>
              </View>
              <Switch
                value={settings.autoFreeTableOnPay}
                onValueChange={(value) =>
                  setSettings((s) => ({ ...s, autoFreeTableOnPay: value }))
                }
                trackColor={{ false: colors.borderSoft, true: colors.success }}
                thumbColor={colors.surface}
                accessibilityLabel="Liberar mesa al cobrar"
              />
            </View>

            <View style={styles.switchRow}>
              <View style={styles.switchCopy}>
                <Text style={styles.label}>Pagar desde la mesa</Text>
                <Text style={styles.desc}>
                  El cliente ve Pagar en la precuenta y paga con Mercado Pago. Hace falta
                  tener MP conectado en el local. La mesa sigue ocupada hasta Liberar
                  (salvo el switch de arriba).
                </Text>
              </View>
              <Switch
                value={settings.tablesidePayAtTableEnabled}
                onValueChange={(value) =>
                  setSettings((s) => ({ ...s, tablesidePayAtTableEnabled: value }))
                }
                trackColor={{ false: colors.borderSoft, true: colors.success }}
                thumbColor={colors.surface}
                accessibilityLabel="Pagar desde la mesa"
                testID="hospitality.tableside.pay-at-table"
              />
            </View>

            {settings.tablesidePayAtTableEnabled ? (
              <>
                <View style={styles.switchRow}>
                  <View style={styles.switchCopy}>
                    <Text style={styles.label}>Propina en el celu</Text>
                    <Text style={styles.desc}>
                      Antes de pagar pueden dejar propina (chips %). Si lo apagás, van
                      directo a Mercado Pago.
                    </Text>
                  </View>
                  <Switch
                    value={settings.tablesideTipEnabled}
                    onValueChange={(value) =>
                      setSettings((s) => ({ ...s, tablesideTipEnabled: value }))
                    }
                    trackColor={{ false: colors.borderSoft, true: colors.success }}
                    thumbColor={colors.surface}
                    accessibilityLabel="Propina en el celu"
                    testID="hospitality.tableside.tip-enabled"
                  />
                </View>

                {settings.tablesideTipEnabled ? (
                  <View style={styles.field}>
                    <Text style={styles.label}>Porcentajes de propina</Text>
                    <Text style={styles.desc}>Separados por coma. Ej: 10, 15, 20</Text>
                    <TextInput
                      style={modalExperience.formInput}
                      value={settings.tablesideTipSuggestedPercents.join(', ')}
                      onChangeText={(value) => {
                        const parts = value
                          .split(/[,\s]+/)
                          .map((p) => Number(p.trim()))
                          .filter((n) => Number.isFinite(n) && n >= 1 && n <= 100)
                          .map((n) => Math.round(n))
                          .slice(0, 6);
                        setSettings((s) => ({
                          ...s,
                          tablesideTipSuggestedPercents: parts.length > 0 ? parts : [10, 15, 20],
                        }));
                      }}
                      keyboardType="numeric"
                      accessibilityLabel="Porcentajes de propina"
                      testID="hospitality.tableside.tip-percents"
                    />
                  </View>
                ) : null}
              </>
            ) : null}

            <View style={styles.switchRow}>
              <View style={styles.switchCopy}>
                <Text style={styles.label}>Dividir en partes iguales</Text>
                <Text style={styles.desc}>
                  En la precuenta pueden ver cuánto es cada uno. Si también pagan desde
                  el celu, cada uno puede pagar su parte. Sin fiado.
                </Text>
              </View>
              <Switch
                value={settings.tablesideSplitEnabled}
                onValueChange={(value) =>
                  setSettings((s) => ({ ...s, tablesideSplitEnabled: value }))
                }
                trackColor={{ false: colors.borderSoft, true: colors.success }}
                thumbColor={colors.surface}
                accessibilityLabel="Dividir en partes iguales"
                testID="hospitality.tableside.split-enabled"
              />
            </View>

            <View style={styles.switchRow}>
              <View style={styles.switchCopy}>
                <Text style={styles.label}>Pedir opinión después de la cuenta</Text>
                <Text style={styles.desc}>
                  Al pedir la cuenta o pagar desde el celu, aparece una nota rápida del 1 al 5.
                  Apagado por defecto.
                </Text>
              </View>
              <Switch
                value={settings.tablesideFeedbackEnabled}
                onValueChange={(value) =>
                  setSettings((s) => ({ ...s, tablesideFeedbackEnabled: value }))
                }
                trackColor={{ false: colors.borderSoft, true: colors.success }}
                thumbColor={colors.surface}
                accessibilityLabel="Pedir opinión después de la cuenta"
                testID="hospitality.tableside.feedback-enabled"
              />
            </View>

            <NotifyCustomerReadyPickupSwitchRow
              style={styles.switchRow}
              value={settings.notifyCustomerReadyPickup}
              onValueChange={(notifyCustomerReadyPickup) =>
                setSettings((s) => ({ ...s, notifyCustomerReadyPickup }))
              }
              disabled={saving}
              hasKitchenTickets
              trackColorOn={colors.success}
              testID="hospitality.tableside.notify-ready-pickup"
            />

            <View style={styles.switchRow}>
              <View style={styles.switchCopy}>
                <Text style={styles.label}>Avisar en la mesa cuando está listo</Text>
                <Text style={styles.desc}>
                  Solo si el cliente dejó el teléfono. El mensaje es distinto: «Tu plato
                  está listo en la mesa.»
                </Text>
              </View>
              <Switch
                value={settings.notifyGuestReadyDineIn}
                onValueChange={(value) =>
                  setSettings((s) => ({ ...s, notifyGuestReadyDineIn: value }))
                }
                trackColor={{ false: colors.borderSoft, true: colors.success }}
                thumbColor={colors.surface}
                accessibilityLabel="Avisar en la mesa cuando está listo"
                testID="hospitality.tableside.notify-ready-dine-in"
              />
            </View>

            <TouchableOpacity
              style={[styles.saveBtn, saving && styles.saveBtnDisabled]}
              onPress={() => void handleSave()}
              disabled={saving}
              accessibilityRole="button"
              accessibilityLabel="Guardar"
            >
              <Text style={styles.saveBtnText}>
                {saving ? 'Guardando…' : 'Guardar'}
              </Text>
            </TouchableOpacity>
          </ScrollView>
        )
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    marginBottom: spacing.sm,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: spacing.sm,
    backgroundColor: ticketColors.tile,
  },
  headerCopy: { flex: 1, minWidth: 0, gap: 2 },
  title: { ...typography.body, color: colors.textPrimary, fontWeight: '700' },
  headerHint: { ...typography.caption, color: colors.textSecondary },
  loader: { marginVertical: spacing.md },
  body: {
    paddingHorizontal: 14,
    paddingTop: 12,
    paddingBottom: 14,
    gap: 12,
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  switchCopy: { flex: 1, minWidth: 0, gap: 2 },
  label: { ...typography.caption, color: colors.textSecondary, fontWeight: '700' },
  desc: { ...typography.caption, color: colors.textSecondary, lineHeight: 18 },
  modeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  modeChip: {
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: ticketColors.tile,
  },
  modeChipSelected: {
    backgroundColor: ticketColors.gold,
    borderColor: ticketColors.gold,
  },
  modeChipText: { ...typography.caption, color: ticketColors.ink2, fontWeight: '700' },
  modeChipTextSelected: { color: '#FFFFFF' },
  fieldsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md },
  field: { flexGrow: 1, flexBasis: 160, minWidth: 0, gap: 6 },
  saveBtn: {
    alignSelf: 'flex-start',
    minHeight: 40,
    borderRadius: 10,
    backgroundColor: colors.accent,
    justifyContent: 'center',
    paddingHorizontal: 14,
  },
  saveBtnDisabled: { opacity: 0.6 },
  saveBtnText: { ...typography.body, color: colors.surface, fontSize: 14, fontWeight: '700' },
});
